import { registerLocaleData } from '@angular/common';

const langToLocaleMapping: Record<string, string> = {
  ar: 'ar-AE',
  bg: 'bg-BG',
  cs: 'cs-CZ',
  da: 'da-DK',
  de: 'de-DE',
  el: 'el-GR',
  en: 'en-GB',
  es: 'es-ES',
  et: 'et-EE',
  fa: 'fa-IR',
  fi: 'fi-FI',
  fr: 'fr-FR',
  he: 'he-IL',
  hi: 'hi-IN',
  hr: 'hr-HR',
  hu: 'hu-HU',
  id: 'id-ID',
  is: 'is-IS',
  it: 'it-IT',
  ja: 'ja-JP',
  km: 'km-KH',
  kn: 'kn-IN',
  ko: 'ko-KR',
  ku: 'ku-KRM',
  lo: 'lo-LA',
  lt: 'lt-LT',
  lv: 'lv-LV',
  mk: 'mk-MK',
  ms: 'ms-MS',
  neutral: 'en-GB',
  nl: 'nl-NL',
  no: 'nn-NO',
  pl: 'pl-PL',
  pt: 'pt-PT',
  'pt-br': 'pt-BR',
  ro: 'ro-RO',
  ru: 'ru-RU',
  sk: 'sk-SK',
  sl: 'sl-SI',
  so: 'so-SO',
  sr: 'sr-Latn',
  sv: 'sv-SE',
  sw: 'sw-KE',
  th: 'th-TH',
  tl: 'fil-PH',
  tr: 'tr-TR',
  uk: 'uk-UA',
  ur: 'ur-PK',
  vi: 'vi-VN',
  'zh-cn': 'zh-CN',
};

const registeredLocales = new Set<string>();
const pendingLocales = new Map<string, Promise<void>>();

const localeImports: Record<string, () => Promise<any>> = {
  ar: () => import('@angular/common/locales/ar'),
  bg: () => import('@angular/common/locales/bg'),
  cs: () => import('@angular/common/locales/cs'),
  da: () => import('@angular/common/locales/da'),
  de: () => import('@angular/common/locales/de'),
  el: () => import('@angular/common/locales/el'),
  en: () => import('@angular/common/locales/en'),
  es: () => import('@angular/common/locales/es'),
  et: () => import('@angular/common/locales/et'),
  fa: () => import('@angular/common/locales/fa'),
  fi: () => import('@angular/common/locales/fi'),
  fr: () => import('@angular/common/locales/fr'),
  he: () => import('@angular/common/locales/he'),
  hi: () => import('@angular/common/locales/hi'),
  hr: () => import('@angular/common/locales/hr'),
  hu: () => import('@angular/common/locales/hu'),
  id: () => import('@angular/common/locales/id'),
  is: () => import('@angular/common/locales/is'),
  it: () => import('@angular/common/locales/it'),
  ja: () => import('@angular/common/locales/ja'),
  km: () => import('@angular/common/locales/km'),
  kn: () => import('@angular/common/locales/kn'),
  ko: () => import('@angular/common/locales/ko'),
  ku: () => import('@angular/common/locales/ku'),
  lo: () => import('@angular/common/locales/lo'),
  lt: () => import('@angular/common/locales/lt'),
  lv: () => import('@angular/common/locales/lv'),
  mk: () => import('@angular/common/locales/mk'),
  ms: () => import('@angular/common/locales/ms'),
  nl: () => import('@angular/common/locales/nl'),
  no: () => import('@angular/common/locales/no'),
  pl: () => import('@angular/common/locales/pl'),
  pt: () => import('@angular/common/locales/pt-PT'),
  'pt-br': () => import('@angular/common/locales/pt'),
  ro: () => import('@angular/common/locales/ro'),
  ru: () => import('@angular/common/locales/ru'),
  sk: () => import('@angular/common/locales/sk'),
  sl: () => import('@angular/common/locales/sl'),
  so: () => import('@angular/common/locales/so'),
  sr: () => import('@angular/common/locales/sr-Latn'),
  sv: () => import('@angular/common/locales/sv'),
  sw: () => import('@angular/common/locales/sw'),
  th: () => import('@angular/common/locales/th'),
  tl: () => import('@angular/common/locales/fil'),
  tr: () => import('@angular/common/locales/tr'),
  uk: () => import('@angular/common/locales/uk'),
  ur: () => import('@angular/common/locales/ur'),
  vi: () => import('@angular/common/locales/vi'),
  'zh-cn': () => import('@angular/common/locales/zh-Hans'),
};

export function isLocaleRegistered(lang: string): boolean {
  return registeredLocales.has(lang);
}

export function registerDynamicLocale(lang: string): Promise<void> {
  if (!lang) { return Promise.resolve(); }
  if (registeredLocales.has(lang)) { return Promise.resolve(); }
  if (pendingLocales.has(lang)) { return pendingLocales.get(lang); }

  const importFn = localeImports[lang] ?? localeImports['en'];

  const promise = importFn().then(
    (locale) => {
      registerLocaleData(locale.default, lang);
      const fullLocaleId = langToLocaleMapping[lang];
      if (fullLocaleId) {
        registerLocaleData(locale.default, fullLocaleId);
      }
      registeredLocales.add(lang);
    },
    () => {
      if (!registeredLocales.has('en')) {
        return localeImports['en']().then((locale) => {
          registerLocaleData(locale.default, 'en');
          registeredLocales.add('en');
        });
      }
    },
  ).finally(() => pendingLocales.delete(lang));

  pendingLocales.set(lang, promise);
  return promise;
}

export function getLocaleId(lang: string): string {
  return langToLocaleMapping[lang] ?? 'en-GB';
}
